import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ArrowUpRight } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, BLOG_URL } from "@/data/site";

// Official logo asset — big_bear_heating&air_logo.png served from /public.
export const Wordmark = ({ className = "h-[84px]" }) => (
  <img
    src="/big-bear-logo.png"
    alt="Big Bear Heating & Air — Manteca, CA"
    className={`${className} w-auto select-none mix-blend-screen`}
    draggable="false"
  />
);

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="main-nav"
      className="sticky top-0 z-50 border-b border-white/10 bg-bear-ink/90 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-24 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" data-testid="nav-logo" aria-label="Big Bear Heating & Air home">
          <Wordmark />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `group relative font-head text-sm font-semibold uppercase tracking-[0.12em] transition-colors duration-300 ${
                  isActive ? "text-bear-cyan" : "text-white/75 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute -bottom-2 left-0 h-0.5 bg-bear-cyan transition-all duration-300 ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
          <a
            href={BLOG_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-link-blog"
            className="group relative flex items-center gap-1 font-head text-sm font-semibold uppercase tracking-[0.12em] text-white/75 transition-colors duration-300 hover:text-white"
          >
            Blog
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            <span className="absolute -bottom-2 left-0 h-0.5 w-0 bg-bear-cyan transition-all duration-300 group-hover:w-full" />
          </a>
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <span
            data-testid="nav-status-pill"
            className="flex items-center gap-2 rounded-full border border-bear-gold/50 px-3.5 py-1.5 font-head text-[11px] font-semibold uppercase tracking-[0.12em] text-bear-gold"
          >
            <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-bear-gold" />
            After-Hours Available
          </span>
          <motion.a
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            href={PHONE_TEL}
            data-testid="nav-call-button"
            className="flex items-center gap-2 rounded-full bg-bear-red px-5 py-2.5 font-head text-sm font-bold tracking-wide text-white shadow-[0_8px_24px_rgba(229,36,37,0.35)]"
          >
            <Phone className="h-4 w-4" />
            {PHONE_DISPLAY}
          </motion.a>
        </div>

        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key={location.pathname + "-menu"}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-bear-ink lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="space-y-1 px-6 py-6">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ x: -16, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.06 * i }}
                >
                  <NavLink
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block py-3 font-display text-3xl tracking-wide ${
                        isActive ? "text-bear-cyan" : "text-white"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.a
                initial={{ x: -16, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.24 }}
                href={BLOG_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-link-blog"
                className="flex items-center gap-2 py-3 font-display text-3xl tracking-wide text-white"
              >
                Blog <ArrowUpRight className="h-6 w-6 text-bear-gold" />
              </motion.a>
              <a
                href={PHONE_TEL}
                data-testid="mobile-menu-call-button"
                className="mt-4 flex items-center justify-center gap-2 rounded-full bg-bear-red py-4 font-head text-base font-bold text-white"
              >
                <Phone className="h-5 w-5" /> Call {PHONE_DISPLAY}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
